import { Component, lazy, Suspense, useEffect, useState, type ComponentType, type ReactNode } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/ScrollToTop";
import AnnouncementBar from "./components/AnnouncementBar";
import SkipToContent from "./components/SkipToContent";
import Index from "./pages/Index";
import { AuthProvider } from "./hooks/useAuth";
import ProtectedRoute from "./components/ProtectedRoute";
import VisitorTrackerBoot from "./components/VisitorTrackerBoot";

const lazyLoadRetryKey = "valley-lazy-load-retry";

const isModuleLoadError = (error: unknown) =>
  /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Load failed/i.test(
    error instanceof Error ? error.message : String(error)
  );

const lazyWithRetry = <T extends ComponentType<unknown>>(factory: () => Promise<{ default: T }>) =>
  lazy(() =>
    factory()
      .then((module) => {
        if (typeof window !== "undefined") window.sessionStorage.removeItem(lazyLoadRetryKey);
        return module;
      })
      .catch((error) => {
        if (
          typeof window !== "undefined" &&
          isModuleLoadError(error) &&
          !window.sessionStorage.getItem(lazyLoadRetryKey)
        ) {
          window.sessionStorage.setItem(lazyLoadRetryKey, "1");
          window.location.reload();
          return new Promise<never>(() => {});
        }

        throw error;
      })
  );

class LazyImportBoundary extends Component<{ children: ReactNode; fallback?: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) return this.props.fallback ?? null;
    return this.props.children;
  }
}

const routeLoadFallback = (
  <div className="min-h-screen flex items-center justify-center bg-background px-4 text-center">
    <div className="max-w-sm">
      <h1 className="text-2xl font-bold mb-2">Page didn’t load</h1>
      <p className="text-muted-foreground mb-4">Refresh once to load the latest version.</p>
      <button type="button" className="text-primary underline" onClick={() => window.location.reload()}>
        Refresh
      </button>
    </div>
  </div>
);

// Non-home routes are code-split to keep the initial JS payload small
const AreasWeServe = lazyWithRetry(() => import("./pages/AreasWeServe"));
const AreaPage = lazyWithRetry(() => import("./pages/AreaPage"));
const ScreenOptions = lazyWithRetry(() => import("./pages/ScreenOptions"));
const ContactPage = lazyWithRetry(() => import("./pages/ContactPage"));
const MaintenancePlans = lazyWithRetry(() => import("./pages/MaintenancePlans"));
const WelcomePage = lazyWithRetry(() => import("./pages/WelcomePage"));
const Warranty = lazyWithRetry(() => import("./pages/Warranty"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const InstantQuote = lazyWithRetry(() => import("./pages/InstantQuote"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const AuthPage = lazyWithRetry(() => import("./pages/Auth"));
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const DashboardMetric = lazyWithRetry(() => import("./pages/DashboardMetric"));
const VisitorsPage = lazyWithRetry(() => import("./pages/VisitorsPage"));

// Defer non-critical floating UI until after the first paint so it does not
// compete with the landing route for initial parse/execute/render time.
const FloatingCTA = lazyWithRetry(() => import("./components/FloatingCTA"));
const MobileCallBanner = lazyWithRetry(() => import("./components/MobileCallBanner"));
const QuickContactWidget = lazyWithRetry(() => import("./components/QuickContactWidget"));
const LiveActivityTicker = lazyWithRetry(() => import("./components/LiveActivityTicker"));
const ChatBot = lazyWithRetry(() => import("./components/ChatBot"));
const CookieConsent = lazyWithRetry(() => import("./components/CookieConsent"));

// Toast / tooltip providers pull the radix-vendor chunk into the initial
// network dependency tree even though no toast or tooltip is shown on first
// paint. Lazy-loading them alongside the other deferred UI removes
// radix-vendor from the Lighthouse "Network dependency tree" critical chain.
const DeferredProviders = lazyWithRetry(() => import("./components/DeferredProviders")) as unknown as React.ComponentType<{ children: ReactNode }>;

const queryClient = new QueryClient();

function App() {
  const [showDeferredUi, setShowDeferredUi] = useState(false);

  useEffect(() => {
    let frame1 = 0;
    let frame2 = 0;

    frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => {
        setShowDeferredUi(true);
      });
    });

    return () => {
      window.cancelAnimationFrame(frame1);
      window.cancelAnimationFrame(frame2);
    };
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <SkipToContent />
            <AnnouncementBar />
            <VisitorTrackerBoot />
            <LazyImportBoundary fallback={routeLoadFallback}>
              <Suspense fallback={null}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/areas" element={<AreasWeServe />} />
                  <Route path="/areas/:slug" element={<AreaPage />} />
                  <Route path="/screen-options" element={<ScreenOptions />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/maintenance" element={<MaintenancePlans />} />
                  <Route path="/welcome" element={<WelcomePage />} />
                  <Route path="/warranty" element={<Warranty />} />
                  <Route path="/instant-quote" element={<InstantQuote />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute requireAdmin>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard/visitors"
                    element={
                      <ProtectedRoute requireAdmin>
                        <VisitorsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard/:metric"
                    element={
                      <ProtectedRoute requireAdmin>
                        <DashboardMetric />
                      </ProtectedRoute>
                    }
                  />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </LazyImportBoundary>

            <LazyImportBoundary>
              <Suspense fallback={null}>
                {showDeferredUi ? (
                  <DeferredProviders>
                    <FloatingCTA />
                    <MobileCallBanner />
                    <QuickContactWidget />
                    <LiveActivityTicker />
                    <ChatBot />
                    <CookieConsent />
                  </DeferredProviders>
                ) : null}
              </Suspense>
            </LazyImportBoundary>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
